import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Progress } from '../../../components/ui/progress';
import { Check, X, Clock, MessageSquare } from 'lucide-react';
import { cn } from '../../../lib/utils';
import type { FilledDataset } from '../../../lib/api';

// Status colors for questionnaires
const statusColors: Record<string, string> = {
  not_started: 'bg-gray-100 text-gray-800',
  in_progress: 'bg-blue-100 text-blue-800',
  submitted: 'bg-cyan-100 text-cyan-800',
  under_review: 'bg-purple-100 text-purple-800',
  more_info_required: 'bg-amber-100 text-amber-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

// Review status badge component
const ReviewStatusBadge = ({ status }: { status: string }) => {
  switch (status) {
    case 'more_info_required':
      return <Badge className="bg-blue-100 text-blue-800">More Information</Badge>;
    case 'rejected':
      return <Badge className="bg-red-100 text-red-800">Declined</Badge>;
    case 'approved':
      return <Badge className="bg-green-100 text-green-800">Approved</Badge>;
    default:
      return <Badge className="bg-gray-100 text-gray-800">Awaiting Review</Badge>;
  }
};

interface ApprovalLevel {
  level: number;
  decision: string;
  reviewer: string | null;
  decided_at: string | null;
  comments: string;
}

interface ApprovalStatus {
  total_levels: number;
  current_level: number | null;
  completed_levels: number;
  levels: ApprovalLevel[];
  is_complete: boolean;
  is_rejected: boolean;
}

interface ApprovalQuestionnaireCardsProps {
  questionnaires: FilledDataset[];
  approvalStatuses: Record<number, ApprovalStatus>;
  onViewQuestionnaire: (id: number) => void;
}

/**
 * Renders risk questionnaire cards and other questionnaire cards with approval status
 * for the Supplier Approval Wizard.
 */
export default function ApprovalQuestionnaireCards({
  questionnaires,
  approvalStatuses,
  onViewQuestionnaire,
}: ApprovalQuestionnaireCardsProps) {
  const riskQuestionnaires = questionnaires.filter(q => q.dataset_type === 'risk');
  const otherQuestionnaires = questionnaires.filter(q => q.dataset_type !== 'risk');

  return (
    <>
      {/* Risk Questionnaires */}
      {riskQuestionnaires.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground tracking-wider mb-3">RISK QUESTIONNAIRES</h4>
          {riskQuestionnaires.map((riskQ) => (
            <Card key={riskQ.id}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold">{riskQ.dataset_name}</h3>
                  <Badge className={statusColors[riskQ.status]}>{riskQ.status.replace(/_/g, ' ')}</Badge>
                </div>
                <div className="flex items-center gap-4 mb-3">
                  <div className="flex-1">
                    <Progress value={riskQ.completion_percentage} className="h-2" />
                  </div>
                  <span className="text-sm font-medium">
                    {riskQ.questions_answered}/{riskQ.questions_total} ({Math.round(riskQ.completion_percentage)}%)
                  </span>
                </div>
                {riskQ.score_percentage !== undefined && (
                  <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg mb-3">
                    <span className="text-sm text-muted-foreground">Risk Score:</span>
                    <span className="font-bold">{Math.round(riskQ.score_percentage || 0)}%</span>
                    {riskQ.passed !== undefined && (
                      <Badge className={riskQ.passed ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                        {riskQ.passed ? 'Passed' : 'Failed'}
                      </Badge>
                    )}
                  </div>
                )}
                <Button variant="outline" size="sm" onClick={() => onViewQuestionnaire(riskQ.id)}>
                  View Full Questionnaire
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Other Questionnaires */}
      {otherQuestionnaires.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground tracking-wider mb-3">QUESTIONNAIRES</h4>
          <div className="space-y-3">
            {otherQuestionnaires.map((q) => {
              const approvalStatus = approvalStatuses[q.id];
              return (
                <Card key={q.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold">{q.dataset_name || 'Questionnaire'}</h3>
                      <ReviewStatusBadge status={q.status} />
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">
                      {q.questions_answered}/{q.questions_total} questions answered
                    </p>
                    {approvalStatus && approvalStatus.total_levels > 0 && (
                      <div className="p-3 bg-gray-50 rounded-lg space-y-2 mb-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">Approval Progress</span>
                          <span className="text-xs text-muted-foreground">
                            {approvalStatus.completed_levels}/{approvalStatus.total_levels} levels
                          </span>
                        </div>
                        <Progress value={(approvalStatus.completed_levels / approvalStatus.total_levels) * 100} className="h-2" />
                        <div className="space-y-1 mt-2">
                          {approvalStatus.levels.map((level) => (
                            <div key={level.level} className={cn(
                              "flex items-center justify-between p-2 rounded border text-sm",
                              level.decision === 'approved' && "bg-green-50 border-green-200",
                              level.decision === 'rejected' && "bg-red-50 border-red-200",
                              level.decision === 'pending' && "bg-white border-gray-200",
                              level.decision === 'request_info' && "bg-amber-50 border-amber-200"
                            )}>
                              <div className="flex items-center gap-2">
                                {level.decision === 'approved' && <Check className="h-3 w-3 text-green-600" />}
                                {level.decision === 'rejected' && <X className="h-3 w-3 text-red-600" />}
                                {level.decision === 'pending' && <Clock className="h-3 w-3 text-gray-400" />}
                                {level.decision === 'request_info' && <MessageSquare className="h-3 w-3 text-amber-600" />}
                                <span>Level {level.level}</span>
                              </div>
                              {level.reviewer && <span className="text-xs text-muted-foreground">{level.reviewer}</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {q.review_notes && (
                      <div className="p-3 bg-gray-50 rounded-lg mb-3">
                        <p className="text-sm">{q.review_notes}</p>
                      </div>
                    )}
                    {q.rejection_reason && (
                      <div className="p-3 bg-red-50 rounded-lg mb-3">
                        <p className="text-sm text-red-800">{q.rejection_reason}</p>
                      </div>
                    )}
                    <Button variant="outline" size="sm" onClick={() => onViewQuestionnaire(q.id)}>
                      View Full Questionnaire
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}
